import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import {
  type DeviceServiceSettings,
  type DeviceServiceStatus,
  deviceStatusTone,
  formatDeviceStatus,
  isDeviceServiceConfigured,
  statusBadgeClass,
} from "@/lib/deviceService";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";
import { schnittstellenIntegrationHref } from "@/pages/stammdaten/schnittstellenIntegrations";

interface TenantSettings {
  device_service?: DeviceServiceSettings;
}

interface ZvtIntegrationPanelProps {
  canWrite: boolean;
}

export function ZvtIntegrationPanel({ canWrite }: ZvtIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [zvtEnabled, setZvtEnabled] = useState(false);
  const [deviceServiceConfigured, setDeviceServiceConfigured] = useState(false);
  const [zvtStatus, setZvtStatus] = useState<DeviceServiceStatus | null>(null);
  const [zvtStatusLoading, setZvtStatusLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.get<TenantSettings>("/api/v1/settings");
      setZvtEnabled(settings.device_service?.enable_zvt ?? false);
      setDeviceServiceConfigured(isDeviceServiceConfigured(settings.device_service));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadZvtStatus = useCallback(async () => {
    setZvtStatusLoading(true);
    try {
      const result = await api.get<DeviceServiceStatus>("/api/v1/settings/device-service/status");
      setZvtStatus(result);
    } catch (e) {
      setZvtStatus({
        connected: false,
        error: e instanceof Error ? e.message : "Status konnte nicht geladen werden.",
        health: null,
        devices: [],
        zvt: null,
      });
    } finally {
      setZvtStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!deviceServiceConfigured) {
      setZvtStatus(null);
      return;
    }
    void loadZvtStatus();
    const interval = window.setInterval(() => {
      void loadZvtStatus();
    }, 10_000);
    return () => window.clearInterval(interval);
  }, [deviceServiceConfigured, loadZvtStatus]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setSuccess(false);
    setError(null);
    try {
      await api.patch("/api/v1/settings/zvt", { enable_zvt: zvtEnabled });
      setSuccess(true);
      void load();
      void loadZvtStatus();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "EC-Terminal-Einstellungen konnten nicht gespeichert werden",
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  const zvtDevice = zvtStatus?.devices.find((d) => d.type === "ZvtTerminal" || d.id === "zvt-terminal");
  const zvtHealthTone = zvtStatus?.zvt?.is_connected
    ? deviceStatusTone(zvtStatus.zvt.payment_status)
    : "red";

  return (
    <form onSubmit={(e) => void onSave(e)} className="space-y-5">
      <FormHint>
        Kartenzahlungen über das EC-Terminal an der Rezeption, im Schnellverkauf und am Gäste-Tablet.
        Die Verbindung zum lokalen CampingDeviceService und die Terminal-Konfiguration finden Sie unter{" "}
        <Link to={schnittstellenIntegrationHref("device-service")} className="text-accent underline">
          CampingDeviceService
        </Link>
        . Terminal-IP und Passwort konfigurieren Sie im{" "}
        <a
          href="http://127.0.0.1:5050/setup"
          target="_blank"
          rel="noreferrer"
          className="text-accent underline"
        >
          lokalen Setup
        </a>
        .
      </FormHint>

      {!deviceServiceConfigured ? (
        <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          Bitte zuerst den{" "}
          <Link to={schnittstellenIntegrationHref("device-service")} className="text-accent underline">
            CampingDeviceService
          </Link>{" "}
          verbinden, bevor ZVT-Zahlungen aktiviert werden können.
        </p>
      ) : null}

      <div className="rounded-xl border border-separator bg-fill-quaternary p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-label">Gesundheitszustand</h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={zvtStatusLoading}
            disabled={!deviceServiceConfigured}
            onClick={() => void loadZvtStatus()}
          >
            Aktualisieren
          </Button>
        </div>

        {!deviceServiceConfigured ? (
          <p className="text-sm text-label-secondary">Keine Verbindung zum CampingDeviceService.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                  zvtStatus?.connected ? "green" : "red",
                )}`}
              >
                {zvtStatus?.connected ? "Service erreichbar" : "Service nicht erreichbar"}
              </span>
              {zvtStatus?.zvt ? (
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(zvtHealthTone)}`}>
                  {zvtStatus.zvt.is_connected ? "Terminal verbunden" : "Terminal getrennt"}
                  {" · "}
                  {formatDeviceStatus(zvtStatus.zvt.payment_status)}
                </span>
              ) : zvtDevice ? (
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                    deviceStatusTone(zvtDevice.status),
                  )}`}
                >
                  {formatDeviceStatus(zvtDevice.status)}
                </span>
              ) : null}
            </div>

            {zvtStatus?.error ? <p className="text-sm text-status-red">{zvtStatus.error}</p> : null}

            {zvtStatus?.zvt ? (
              <dl className="grid gap-1 text-sm text-label-secondary sm:grid-cols-2">
                {zvtStatus.zvt.terminal_id ? (
                  <div>
                    <dt className="inline">Terminal-ID: </dt>
                    <dd className="inline text-label">{zvtStatus.zvt.terminal_id}</dd>
                  </div>
                ) : null}
                {zvtStatus.zvt.host ? (
                  <div>
                    <dt className="inline">Host: </dt>
                    <dd className="inline text-label">
                      {zvtStatus.zvt.host}
                      {zvtStatus.zvt.port ? `:${zvtStatus.zvt.port}` : ""}
                    </dd>
                  </div>
                ) : null}
                {zvtStatus.zvt.message ? (
                  <div className="sm:col-span-2">
                    <dt className="inline">Meldung: </dt>
                    <dd className="inline text-label">{zvtStatus.zvt.message}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
          </>
        )}
      </div>

      <label className="flex items-center gap-3 rounded-xl border border-separator bg-fill-quaternary p-4 text-sm">
        <input
          type="checkbox"
          className="rounded border-separator text-accent focus:ring-accent"
          checked={zvtEnabled}
          onChange={(e) => setZvtEnabled(e.target.checked)}
          disabled={!canWrite || !deviceServiceConfigured}
        />
        <span className="font-medium text-label">EC-Terminal-Zahlungen aktivieren</span>
      </label>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">EC-Terminal-Einstellungen gespeichert.</p> : null}
      {canWrite ? (
        <div className="flex justify-end border-t border-separator pt-4">
          <Button type="submit" loading={saving} disabled={!deviceServiceConfigured}>
            ZVT speichern
          </Button>
        </div>
      ) : null}
    </form>
  );
}
